import { z } from 'zod';
import { Console } from '@utils';
import { NEXT_PUBLIC_AMPLITUDE_API_KEY } from '@config/environment';
import { usePersistState } from './usePersistState';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { EventTrackingSchemas } from '@validators/Events';
import { ScreenEventType } from '@validators/Events/Screen';
import { useOrderState } from '@hooks/useOrderState';
import { useAuth } from './useAuth';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'next/router';
import { usePermissions } from './usePermissions';
import { flattenObject } from '@utils/collection';
import { User } from '@interfaces';
import { InteractionEventTypes } from '@validators/Events/Interactions';
import {
  FormIdentifiersForEvents,
  InputIdentifiersForEvents,
} from '@validators/Events/Interactions/others';
import { trackEvent, identifyUser } from '@services/amplitude';

interface ScreenView {
  event: string;
  screenInstanceId?: string;
  screenType?: ScreenEventType;
}

export function useEventTracking() {
  const { appUser } = useAuth();
  const [userId, setUserId, clearUserId] = usePersistState('ukey', '');
  const { orderUuid } = useOrderState();
  const isBrowser = typeof window !== 'undefined';
  const { userCan } = usePermissions();

  const router = useRouter();
  const routeKey = `screen_instance_id_${router.pathname}`;
  const [screenInstanceId, setScreenInstanceId] = usePersistState(routeKey, '');

  const lastScreenViewTime = useRef<number>(0);
  const lastScreenView = useRef<ScreenView | null>(null);

  const BASE_PROPS = useCallback(
    () => ({
      meki_session_id: '-',
      user_id: undefined,
      user_uuid: undefined,
      timestamp: new Date().toISOString(),
      order_uuid: orderUuid,
      screen_instance_id: screenInstanceId,
    }),
    [orderUuid, screenInstanceId],
  );

  const handleScreenViewEvent = useCallback(
    (event: string, screenInstanceId?: string) => {
      const now = Date.now();
      const timeSinceLastView = now - lastScreenViewTime.current;
      const isSameScreenAsBefore =
        lastScreenView.current?.event === event &&
        lastScreenView.current?.screenInstanceId === screenInstanceId;

      if (
        timeSinceLastView < 1000 ||
        isSameScreenAsBefore ||
        screenInstanceId == ''
      ) {
        return false;
      }

      lastScreenViewTime.current = now;
      lastScreenView.current = {
        event,
        screenInstanceId,
      };

      if (timeSinceLastView < 1000 || isSameScreenAsBefore) {
        return false;
      }
      return true;
    },
    [],
  );

  const track = useCallback(
    async <T extends keyof typeof EventTrackingSchemas>(
      event: T,
      eventProps?: z.infer<typeof EventTrackingSchemas[T]>,
      user?: User,
    ) => {
      if (!isBrowser || userCan('MANAGE_USER')) return;

      try {
        const userData = user
          ? user.segmentationInfo
          : appUser?.segmentationInfo;

        const basePropsWithSegmentationsTmp = {
          ...BASE_PROPS(),
          user_id: appUser?.id ?? undefined,
          user_uuid: appUser?.uuid ?? undefined,
          screen_instance_id: screenInstanceId,
          user_segmentation_data: {
            total_money_disbursed: userData?.totalMoneyDisbursed,
            total_orders_paid: appUser?.segmentationInfo?.totalOrdersPaid,
            days_since_last_order: userData?.daysSinceLastOrder,
          },
        };
        const basePropsWithSegmentations = flattenObject(
          basePropsWithSegmentationsTmp,
        );

        const isScreenViewEvent =
          event?.toString().includes('screen') &&
          event?.toString().includes('view');

        if (isScreenViewEvent) {
          const shouldSendViewEvent = handleScreenViewEvent(
            event.toString(),
            screenInstanceId,
          );
          if (!shouldSendViewEvent) return;
        }

        Console.log('[AMPLITUDE][TRACK]', {
          event,
          baseProps: basePropsWithSegmentations,
          eventProps,
          sendAmplitude: !!NEXT_PUBLIC_AMPLITUDE_API_KEY,
        });

        if (NEXT_PUBLIC_AMPLITUDE_API_KEY) {
          const schema = EventTrackingSchemas[event];
          if (schema && eventProps) {
            schema.parse({
              ...basePropsWithSegmentations,
              ...eventProps,
            });
          }
          void trackEvent(event, {
            ...basePropsWithSegmentations,
            ...eventProps,
          });
        }
      } catch (error) {
        Console.error('[AMPLITUDE][TRACK] Error:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBrowser, BASE_PROPS, handleScreenViewEvent],
  );

  const trackFormInput = useCallback(
    (
      value: unknown,
      inputIdentifier: InputIdentifiersForEvents,
      formIdentifier: FormIdentifiersForEvents,
    ) => {
      if (value === '') return;
      void track(InteractionEventTypes.FormInput, {
        value,
        input_name: inputIdentifier,
        form_name: formIdentifier,
      });
    },
    [track],
  );

  const identify = useCallback(
    (newUserId: string, userProps: Record<string, any>) => {
      if (!isBrowser) return;
      try {
        Console.log('[AMPLITUDE][IDENTIFY]', {
          sendAmplitude: !!NEXT_PUBLIC_AMPLITUDE_API_KEY,
          newUserId,
          userProps,
        });
        setUserId(newUserId);
        identifyUser(newUserId, userProps);
      } catch (error) {
        Console.error('[AMPLITUDE][IDENTIFY] Error:', error);
      }
    },
    [isBrowser, setUserId],
  );

  useEffect(() => {
    const updateScreenInstanceOnRouteChange = () => {
      if (!screenInstanceId) {
        setScreenInstanceId(uuidv4());
      }
    };
    updateScreenInstanceOnRouteChange();
  }, [screenInstanceId, setScreenInstanceId]);

  const functions = useMemo(() => {
    return { track, identify, trackFormInput };
  }, [track, identify, trackFormInput]);

  return functions;
}
